import { useState } from 'react';

import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-light-svg-icons';
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  getISOWeek,
  isWithinInterval,
  startOfMonth,
  startOfToday,
  startOfWeek
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Interval } from 'components/types';

import { TEXT } from 'constants/colors';

import { formatByLocale, getMonthInterval, getWeekInterval } from 'utils/date';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { DatePicker, FontAwesomeSvgIcon } from 'components/shared';

type Props = {
  view?: 'week' | 'month';
  defaultInterval?: Interval;
  minDate?: Date;
  maxDate?: Date;
  onSelected: (interval: Interval) => void;
};

const DateIntervalStyles = styled.div<Pick<Props, 'view'>>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-right: 40px;

  .date-picker {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  h2 {
    margin: 0;
    min-width: ${(props) => (props.view === 'week' ? 400 : 300)}px;
    text-align: center;
    color: ${TEXT};
    font-family: 'signikasemibold';

    .week-nr {
      display: inline-block;
      margin: 0 5px;
    }
  }

  button {
    margin: 0 5px;
  }
`;

export const getIntervalTitle = ({ startDate, endDate }: Interval) => {
  const startYear = formatByLocale(startDate, 'y');
  const endYear = formatByLocale(endDate, 'y');
  const startDateFormat = startYear === endYear ? 'd MMMM' : 'd MMMM y';
  const endDateFormat = startYear === endYear ? 'd MMMM, y' : 'd MMMM y';

  return `${formatByLocale(startDate, startDateFormat)} - ${formatByLocale(
    endDate,
    endDateFormat
  )}`;
};

export default function DateInterval({
  view = 'month',
  defaultInterval,
  minDate,
  maxDate,
  onSelected
}: Props) {
  const { t } = useTranslation('common');
  const isMonthView = view === 'month';
  const isWeekView = view === 'week';
  const [interval, setInterval] = useState(
    defaultInterval || (isMonthView ? getMonthInterval() : getWeekInterval())
  );
  const datePickerDate = isWithinInterval(startOfToday(), {
    start: interval.startDate,
    end: interval.endDate
  })
    ? startOfToday()
    : interval.startDate;

  const createNewInterval = (date: 'prev' | 'next' | Date) => {
    let newInterval: Interval;
    if (typeof date === 'string') {
      const amount = date === 'prev' ? -1 : 1;
      const newDate = isMonthView
        ? addMonths(interval.startDate, amount)
        : addWeeks(interval.startDate, amount);
      newInterval = {
        startDate: isMonthView
          ? startOfMonth(newDate)
          : startOfWeek(newDate, { weekStartsOn: 1 }),
        endDate: isMonthView
          ? endOfMonth(newDate)
          : endOfWeek(newDate, { weekStartsOn: 1 })
      };
    } else {
      newInterval = isMonthView
        ? getMonthInterval(date)
        : getWeekInterval(date);
    }
    setInterval(newInterval);
    onSelected(newInterval);
  };

  return (
    <DateIntervalStyles className="date-interval" view={view}>
      <h2>
        {getIntervalTitle(interval)}
        {isWeekView && (
          <span className="week-nr">
            ({t('week')} {getISOWeek(interval.startDate)})
          </span>
        )}
      </h2>
      <Tooltip
        title={t(view === 'month' ? 'previousMonth' : 'previousWeek') || ''}
        arrow
      >
        <IconButton
          color="primary"
          onClick={() => createNewInterval('prev')}
          className="prev-btn"
          size="small"
          disabled={minDate && datePickerDate <= minDate}
        >
          <FontAwesomeSvgIcon icon={faChevronLeft} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={t(view === 'month' ? 'nextMonth' : 'nextWeek') || ''}
        arrow
      >
        <IconButton
          color="primary"
          onClick={() => createNewInterval('next')}
          className="next-btn"
          size="small"
          disabled={maxDate && datePickerDate >= maxDate}
        >
          <FontAwesomeSvgIcon icon={faChevronRight} fontSize="small" />
        </IconButton>
      </Tooltip>
      <div className="date-picker">
        <DatePicker
          onChange={(date) => date && createNewInterval(date)}
          date={datePickerDate}
          minDate={minDate}
          maxDate={maxDate}
          monthYearView={isMonthView}
        />
      </div>
    </DateIntervalStyles>
  );
}
